export type PlanTier = 'free' | 'growth' | 'pro' | 'enterprise';

export interface TierConfig {
  id: PlanTier;
  name: string;
  price: number;
  priceLabel: string;
  resolutionLimit: number;
  resolutionLabel: string;
  features: string[];
  privileges: TierPrivileges;
  stripePriceId?: string;
  popular?: boolean;
}

export interface TierPrivileges {
  basicWidget: boolean;
  integrations: boolean;
  integrationCount: number | 'unlimited';
  unlimitedUsers: boolean;
  prioritySupport: boolean;
  whiteLabel: boolean;
  sla: boolean;
  advancedAnalytics: boolean;
  multimodalReplies: boolean;
  customBranding: boolean;
  dedicatedOnboarding: boolean;
  apiAccess: boolean;
}

export const TIER_ORDER: PlanTier[] = ['free', 'growth', 'pro', 'enterprise'];

export const TIER_CONFIGS: Record<PlanTier, TierConfig> = {
  free: {
    id: 'free',
    name: 'Free',
    price: 0,
    priceLabel: '$0/mo',
    resolutionLimit: 150,
    resolutionLabel: '150 resolutions/mo',
    features: [
      'Plush chat widget',
      'Basic knowledge base sync',
      'Confidence scores on replies',
      'Email support',
      '1 integration',
    ],
    privileges: {
      basicWidget: true,
      integrations: true,
      integrationCount: 1,
      unlimitedUsers: false,
      prioritySupport: false,
      whiteLabel: false,
      sla: false,
      advancedAnalytics: false,
      multimodalReplies: false,
      customBranding: false,
      dedicatedOnboarding: false,
      apiAccess: false,
    },
  },
  growth: {
    id: 'growth',
    name: 'Growth',
    price: 149,
    priceLabel: '$149/mo',
    resolutionLimit: 1500,
    resolutionLabel: '1,500 resolutions/mo',
    features: [
      'Everything in Free',
      'Self-updating knowledge engine',
      'Jira / Slack / Docs sync',
      'One-click article approval',
      'Priority email support',
      '3 integrations',
    ],
    privileges: {
      basicWidget: true,
      integrations: true,
      integrationCount: 3,
      unlimitedUsers: false,
      prioritySupport: true,
      whiteLabel: false,
      sla: false,
      advancedAnalytics: false,
      multimodalReplies: false,
      customBranding: false,
      dedicatedOnboarding: false,
      apiAccess: false,
    },
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    price: 399,
    priceLabel: '$399/mo',
    resolutionLimit: 6000,
    resolutionLabel: '6,000 resolutions/mo',
    popular: true,
    features: [
      'Everything in Growth',
      'Multimodal replies (diagrams, simulators)',
      'Citations + source confidence',
      'Advanced analytics dashboard',
      'Zendesk / Intercom / Freshdesk embed',
      'Slack escalation workflows',
      'Dedicated onboarding call',
      'Unlimited integrations',
      'Unlimited team members',
      'Priority support',
    ],
    privileges: {
      basicWidget: true,
      integrations: true,
      integrationCount: 'unlimited',
      unlimitedUsers: true,
      prioritySupport: true,
      whiteLabel: false,
      sla: false,
      advancedAnalytics: true,
      multimodalReplies: true,
      customBranding: false,
      dedicatedOnboarding: true,
      apiAccess: true,
    },
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    price: 0,
    priceLabel: 'Custom',
    resolutionLimit: Infinity,
    resolutionLabel: 'Unlimited resolutions',
    features: [
      'Everything in Pro',
      'White-label widget',
      'Custom SLA agreement',
      'Dedicated account manager',
      'Custom integrations',
      'SSO / SAML',
      'On-premise deployment option',
      'Custom contract & invoicing',
    ],
    privileges: {
      basicWidget: true,
      integrations: true,
      integrationCount: 'unlimited',
      unlimitedUsers: true,
      prioritySupport: true,
      whiteLabel: true,
      sla: true,
      advancedAnalytics: true,
      multimodalReplies: true,
      customBranding: true,
      dedicatedOnboarding: true,
      apiAccess: true,
    },
  },
};

export function getTierConfig(tier: PlanTier): TierConfig {
  return TIER_CONFIGS[tier] || TIER_CONFIGS.free;
}

export function canAccessFeature(
  tier: PlanTier,
  feature: keyof TierPrivileges
): boolean {
  return TIER_CONFIGS[tier]?.privileges[feature] === true;
}

export function getIntegrationLimit(tier: PlanTier): number | 'unlimited' {
  return TIER_CONFIGS[tier]?.privileges.integrationCount ?? 1;
}
